import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';

async function fetchScoreSummary(params: { dateFrom?: string; dateTo?: string }) {
  const qs = params.dateFrom && params.dateTo ? `?dateFrom=${params.dateFrom}&dateTo=${params.dateTo}` : '';
  const res = await fetch(`/api/analytics/score-summary${qs}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch score summary');
  return res.json();
}

function computeBucketsFromHistogram(hist: Array<{ from: number; to: number; n: number }>) : Record<string, number> | undefined {
  if (!Array.isArray(hist)) return undefined;
  // Use midpoint of each histogram bucket to classify into the requested ranges.
  // Ranges chosen (inclusive):
  //  - '0-25'   -> critical
  //  - '26-40'  -> warning
  //  - '41-65'  -> info (blue)
  //  - '66-100' -> green
  // Note: using midpoints avoids attempting to split individual histogram buckets.
  const buckets: Record<string, number> = {
    '0-25': 0,
    '26-40': 0,
    '41-65': 0,
    '66-100': 0,
  };
  for (const h of hist) {
    const n = h.n || 0;
    // handle malformed ranges defensively
    const from = typeof h.from === 'number' ? h.from : 0;
    const to = typeof h.to === 'number' ? h.to : from;
    const mid = (from + to) / 2;
    if (mid <= 25) buckets['0-25'] += n;
    else if (mid <= 40) buckets['26-40'] += n;
    else if (mid <= 65) buckets['41-65'] += n;
    else buckets['66-100'] += n;
  }
  return buckets;
}

function SingleBar({ mean }: { mean: number | null }) {
  // Colors for ranges: poor, below average, good, excellent
  const ranges = [
    { max: 25, color: '#ef4444' }, // red
    { max: 40, color: '#f59e0b' }, // yellow
    { max: 65, color: '#3b82f6' }, // blue
    { max: 100, color: '#16a34a' }, // green
  ];

  const pct = typeof mean === 'number' && !Number.isNaN(mean) ? Math.max(0, Math.min(100, Math.round(mean))) : 0;
  const range = ranges.find(r => pct <= r.max) || ranges[ranges.length - 1];
  const bg = range.color;
  const label = pct <= 25 ? 'Poor' : pct <= 40 ? 'Average' : pct <= 65 ? 'Good' : 'Excellent';

  if (mean == null) {
    return (
      <div className="flex items-center justify-center w-full h-20">
        <div className="text-sm text-slate-500">No scored audits</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full">
      <div className="w-full" role="img" aria-label="Score distribution bar">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div data-testid="score-distribution-bar" className="w-full h-6 rounded-md bg-slate-100 overflow-hidden">
                <div className="h-6 rounded-md" style={{ width: `${pct}%`, backgroundColor: bg }} />
              </div>
            </TooltipTrigger>
            <TooltipContent side="top">
              <div className="text-xs">{`Score: ${pct}% - ${label}`}</div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
}

export default function ScoreSummaryCard({ dateFrom, dateTo }: { dateFrom?: string; dateTo?: string }) {
  const key = ['/api/analytics/score-summary', dateFrom ?? '', dateTo ?? ''] as const;
  const { data, isLoading, error } = useQuery({ 
    queryKey: key, 
    queryFn: () => fetchScoreSummary({ dateFrom, dateTo }), 
    staleTime: 1000 * 60 * 2 
  });

  if (isLoading) return (
    <Card className="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-sm">
      <CardContent>
        <div className="text-sm text-slate-400">Loading score summary...</div>
      </CardContent>
    </Card>
  );

  if (error || !data) {
    // Show placeholder donut so dashboard remains visually consistent even when analytics endpoint fails
  const placeholderBuckets = { '0-25': 0, '26-40': 0, '41-65': 0, '66-100': 0 };
    return (
      <Card className="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-sm">
        <CardContent>
          <h4 className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">Score Distribution</h4>
          <div className="flex items-center justify-center h-28">
              <div className="w-full max-w-xs">
              <SingleBar mean={null} />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Accept both old shape { averageScore, countScored, buckets } and new shape { mean, count, histogram }
  const mean = (data.mean ?? data.averageScore ?? data.mean) as number | null;
  const count = (data.count ?? data.countScored ?? data.scoredCount ?? 0) as number;
  let buckets = data.buckets as Record<string, number> | undefined;
  if (!buckets && Array.isArray(data.histogram)) {
    buckets = computeBucketsFromHistogram(data.histogram);
  }

  // Defensive defaults
  // Show numeric average here without percent sign (percent remains in the Average Score tile)
  const avgDisplay = mean != null ? `${mean}` : '—';

  const finalBuckets = buckets || { '0-39': 0, '40-59': 0, '60-79': 0, '80-100': 0 };

  // Ensure we return the new keyset so visuals and tooltips match requested ranges
  const normalizedBuckets = {
    '0-25': (finalBuckets['0-25'] as number) || (finalBuckets['0-39'] as number) || 0,
    '26-40': (finalBuckets['26-40'] as number) || (finalBuckets['40-59'] as number) || 0,
    '41-65': (finalBuckets['41-65'] as number) || (finalBuckets['60-79'] as number) || 0,
    '66-100': (finalBuckets['66-100'] as number) || (finalBuckets['80-100'] as number) || 0,
  };

  return (
    <Card className="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-sm">
      <CardContent>
        <h4 className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">Score Distribution</h4>
        <div className="flex flex-col items-center justify-center h-28">
          <div className="w-full max-w-xs">
            <SingleBar mean={mean} />
          </div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-2">Distribution of audit scores</div>
        </div>
      </CardContent>
    </Card>
  );
}
